import { useState, useRef, ChangeEvent, ReactNode, MutableRefObject, useMemo } from 'react'
import { MenuItem, MenuList, Typography } from '@carto/meridian-ds/components'
import { TextField, Popper, ClickAwayListener, TextFieldProps, Paper, Box, styled } from '@mui/material'

const SoftDisabledMenuItem = styled(MenuItem)(() => ({
  pointerEvents: 'none'
}))

const DEFAULT_MAX_SUGGESTIONS = 100

type Suggestion = {
  /** This value will replace current word when suggestion is selected */
  value: string
}

type TextFieldWithSuggestionsProps<T extends Suggestion> = TextFieldProps & {
  suggestionRegexp: RegExp
  getSuggestions: (currentWord: string) => T[]

  renderSuggestion?: (suggestion: T, currentWord?: string) => React.ReactNode
  renderTextField?: (textFieldProps: TextFieldProps) => React.ReactNode
  maxSuggestions?: number
  maxSuggestionExceededMessage?: ReactNode
  noSuggestionsMessage?: ReactNode

  textInputRef?: MutableRefObject<HTMLInputElement | null>
}

export const defaultFilterSuggestions = <T extends Suggestion>(options: T[], currentWord?: string) => {
  return options.filter((option) => option.value.includes(currentWord || ''))
}

function findCurrentWordStart(value: string, cursorPosition: number): number {
  const uptoCursor = value.slice(0, cursorPosition)
  const lastWhitespaceIndex = Math.max(
    uptoCursor.lastIndexOf(' '),
    uptoCursor.lastIndexOf('\n'),
    uptoCursor.lastIndexOf('\t')
  )
  return lastWhitespaceIndex === -1 ? 0 : lastWhitespaceIndex + 1
}

function useTextFieldWithSuggestions<T extends Suggestion>({
  anchorElement,
  onChange,
  suggestionRegexp,
  renderSuggestion = defaultRenderSuggestion,
  getSuggestions,
  maxSuggestions = DEFAULT_MAX_SUGGESTIONS,
  maxSuggestionExceededMessage,
  noSuggestionsMessage,
  textInputRef: textInputRefFromProps,
  onKeyDown
}: TextFieldWithSuggestionsProps<T> & { anchorElement: HTMLElement | null }): {
  textFieldProps: TextFieldProps
  textInputRef: React.RefObject<HTMLInputElement>
  popover: React.ReactNode
} {
  const textInputRefLocal = useRef<HTMLInputElement | null>(null)
  const textInputRef = textInputRefFromProps || textInputRefLocal
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null)
  const [currentWord, setCurrentWord] = useState<string | undefined>()
  const [open, setOpen] = useState(false)
  const [popperIsTop, setPopperIsTop] = useState(false)

  const popperPlacementListener = useMemo(
    () => ({
      name: 'isTopChecker',
      enabled: true,
      phase: 'main' as const,
      fn({ state }) {
        if (state.placement.includes('top')) {
          setPopperIsTop(true)
        } else {
          setPopperIsTop(false)
        }
      }
    }),
    []
  )
  const hideSuggestions = () => {
    setOpen(false)
    textInputRef.current?.focus()
  }

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const textField = event.target as HTMLInputElement
    const cursorPosition = textField.selectionStart

    if (cursorPosition !== null) {
      const currentWordStart = findCurrentWordStart(textField.value, cursorPosition)
      const currentWord = textField.value.substring(currentWordStart, cursorPosition)

      setCurrentWord(currentWord)

      if (currentWord.match(suggestionRegexp)) {
        setOpen(true)
      } else {
        hideSuggestions()
      }
    }
    onChange?.(event)
  }

  const handleSuggestionClick = <T extends Suggestion>(option: T): void => {
    const textField = textInputRef.current as HTMLInputElement
    if (!textField) return

    const cursorPosition = textField.selectionStart
    const currentValue = textField.value

    if (!cursorPosition) return

    const currentWordStart = findCurrentWordStart(textField.value, cursorPosition)
    const newValue = currentValue.slice(0, currentWordStart) + option.value + currentValue.slice(cursorPosition)

    textField.value = newValue
    textField.selectionStart = currentWordStart + option.value.length
    textField.selectionEnd = currentWordStart + option.value.length

    onChange?.({ target: textField } as ChangeEvent<HTMLInputElement>)
    hideSuggestions()
  }

  const handleTextFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (open) {
      const arrowTriggerKey = popperIsTop ? 'ArrowUp' : 'ArrowDown'
      if (event.key === arrowTriggerKey || event.key === 'Tab') {
        const menuItems = menuRef?.querySelectorAll<HTMLElement>('[role=menuitem]')
        if (!menuItems) return

        const firstMenuItem = popperIsTop ? menuItems[menuItems.length - 1] : menuItems[0]
        firstMenuItem?.focus()
        event.preventDefault()
        return
      }

      if (event.key === 'Escape') {
        hideSuggestions()
        return
      }
    }
    onKeyDown?.(event)
  }

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'Escape') {
      hideSuggestions()
    }
  }

  let shownSuggestions = open && currentWord ? getSuggestions(currentWord) : []

  const tooManySuggestions = shownSuggestions.length > maxSuggestions

  shownSuggestions = tooManySuggestions ? shownSuggestions.slice(0, maxSuggestions) : shownSuggestions

  if (open && shownSuggestions.length === 0 && !noSuggestionsMessage) {
    hideSuggestions()
  }

  const popover = (
    <Popper anchorEl={anchorElement} placement="bottom-start" open={open} modifiers={[popperPlacementListener]}>
      <ClickAwayListener onClickAway={hideSuggestions}>
        <Paper tabIndex={-1} ref={setMenuRef} style={{ width: anchorElement?.clientWidth }}>
          <MenuList onKeyDown={handleMenuKeyDown}>
            {shownSuggestions.length === 0 && (
              <SoftDisabledMenuItem>
                <Typography variant="body2" color="text.secondary">
                  {noSuggestionsMessage}
                </Typography>
              </SoftDisabledMenuItem>
            )}
            {shownSuggestions.map((suggestion, i) => (
              <MenuItem key={i} onClick={() => handleSuggestionClick(suggestion)}>
                {renderSuggestion(suggestion, currentWord)}
              </MenuItem>
            ))}
          </MenuList>

          {tooManySuggestions && maxSuggestionExceededMessage && (
            <SoftDisabledMenuItem>
              <Box sx={{ pb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {maxSuggestionExceededMessage}
                </Typography>
              </Box>
            </SoftDisabledMenuItem>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  )

  return {
    textFieldProps: {
      inputRef: textInputRef,
      onChange: handleTextFieldChange,
      onKeyDown: handleTextFieldKeyDown
    },
    textInputRef,
    popover
  }
}

function defaultRenderSuggestion<T extends Suggestion>(suggestion: T): ReactNode {
  return <>{suggestion.value}</>
}

function defaultRenderTextField(textFieldProps: TextFieldProps) {
  return <TextField {...textFieldProps} />
}

function TextFieldWithSuggestions<T extends Suggestion>({
  onChange,
  suggestionRegexp,
  renderSuggestion = defaultRenderSuggestion,
  renderTextField = defaultRenderTextField,
  getSuggestions,
  maxSuggestions = DEFAULT_MAX_SUGGESTIONS,
  maxSuggestionExceededMessage,
  noSuggestionsMessage,
  textInputRef,
  onKeyDown,
  ...otherProps
}: TextFieldWithSuggestionsProps<T>) {
  const rootRef = useRef<HTMLDivElement>(null)
  const { textFieldProps, popover } = useTextFieldWithSuggestions({
    anchorElement: rootRef.current,
    textInputRef,
    onChange,
    suggestionRegexp,
    renderSuggestion,
    getSuggestions,
    maxSuggestions,
    maxSuggestionExceededMessage,
    noSuggestionsMessage,
    onKeyDown
  })

  return (
    <div ref={rootRef}>
      {renderTextField({ ...textFieldProps, ...otherProps })}
      {popover}
    </div>
  )
}

export default TextFieldWithSuggestions
